import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShoppingBasket, 
  faClock,
  faStore,
  faCalendarAlt,
  faTimes,
  faInfoCircle,
  faCheck,
  faArrowRight,
  faCamera,
  faCar
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import { theme } from '../../../styles/theme';
import { useLocation } from 'react-router-dom';

// Import placeholder image - replace with actual farmshop image
// Import custom hook to force retail section
import { useRetailSection } from '../../../hooks/useRetailSection';
import config from '../../../config/environment';

const farmshopImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/BelvoirEngine-SamChurchill-9195.webp';

const farmshopBanImage = 'https://BelvoirCastle.b-cdn.net/Banners/farmshop-2.webp';

const farmshopLogo = 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/Belvoir%20Farmshop%20Copper.webp';

const OFFERINGS = [
  {
    title: 'Estate-Grown Produce',
    text: 'Seasonal vegetables and fruits grown on the Belvoir Estate, harvested at their peak for maximum flavor and nutritional value.',
  },
  {
    title: 'Local Artisan Products',
    text: 'Carefully selected cheeses, preserves, and baked goods from skilled artisans in the Vale of Belvoir and surrounding counties.',
  },
  {
    title: 'Seasonal Specialties',
    text: 'Our offerings change with the seasons, ensuring you always have access to the freshest, most flavorful produce throughout the year.',
  },
  {
    title: 'The Butcher',
    text: 'Our butchers prepare estate beef, lamb and charcuterie beside baskets of fresh greens.',
  },
];

const SHOP_PHOTOS = [
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/IMG_3674.webp',
    alt: 'Vegetables at Belvoir Farm Shop',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/IMG_3682.webp',
    alt: 'Fruit and vegetables at Belvoir Farm Shop',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/IMG_3703-2.webp',
    alt: 'Onions and shallots at Belvoir Farm Shop',
    position: 'center 78%',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/BelvoirEngine-SamChurchill-9089.webp',
    alt: 'Estate beef at the Belvoir Farm Shop butcher',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/BelvoirEngine-SamChurchill-9150.webp',
    alt: 'The Belvoir Farm Shop butcher',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/BelvoirEngine-SamChurchill-0733.webp',
    alt: 'Chipolatas at the Belvoir Farm Shop butcher',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/BelvoirEngine-SamChurchill-9095.webp',
    alt: 'Cheeses at Belvoir Farm Shop',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/BelvoirEngine-SamChurchill-9173.webp',
    alt: 'Pies at Belvoir Farm Shop',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/BelvoirEngine-SamChurchill-9209.webp',
    alt: 'Belvoir Castle wines and preserves in the Farm Shop',
    position: 'center',
  },
];

const STORIES = [
  { ...OFFERINGS[0], photo: SHOP_PHOTOS[0], ratio: '3 / 2', side: 'right' },
  { ...OFFERINGS[1], photo: SHOP_PHOTOS[6], ratio: '4 / 5', side: 'left' },
  { ...OFFERINGS[2], photo: SHOP_PHOTOS[1], ratio: '1 / 1', side: 'right' },
  { ...OFFERINGS[3], photo: SHOP_PHOTOS[4], ratio: '5 / 4', side: 'left', position: 'center 62%' },
];

const MORE_PHOTOS = [
  SHOP_PHOTOS[2],
  SHOP_PHOTOS[3],
  SHOP_PHOTOS[5],
  SHOP_PHOTOS[7],
  SHOP_PHOTOS[8],
];

const WINE_PHOTOS = [
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/Christmas%20Wine%20(11%20of%2011).webp',
    alt: 'Belvoir Castle wines',
    position: 'center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/Christmas%20Wine%20(3%20of%2011)%20(1).webp',
    alt: 'Pouring a Belvoir Castle wine',
    position: 'right center',
  },
  {
    src: 'https://BelvoirCastle.b-cdn.net/Retail%20village/Farmshop/Christmas%20Wine%20(8%20of%2011).webp',
    alt: 'Glasses of Belvoir Castle wine',
    position: 'center 55%',
  },
];

const FarmshopContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const Hero = styled.section`
  position: relative;
  min-height: calc(100dvh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
  padding-bottom: 8rem;
  color: #fff;
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(16, 29, 64, 0.9) 0%,
    rgba(16, 29, 64, 0.5) 34%,
    rgba(16, 29, 64, 0.12) 64%,
    rgba(16, 29, 64, 0.2) 100%
  );
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: clamp(2.5rem, 6vw, 4.5rem) 1.5rem clamp(3rem, 7vw, 4.75rem);
  text-align: center;
`;

const HeroHeading = styled.h1`
  margin: 0 0 0.85rem;
`;

const HeroLogo = styled.img`
  display: block;
  width: min(230px, 56vw);
  height: auto;
  filter:
    brightness(0)
    invert(1)
    drop-shadow(0 1px 1px rgba(0, 0, 0, 0.85))
    drop-shadow(0 4px 10px rgba(0, 0, 0, 0.7))
    drop-shadow(0 14px 28px rgba(0, 0, 0, 0.55));
`;

const HeroSubtitle = styled.p`
  margin: 0 0 2.35rem;
  max-width: 36rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: clamp(0.69rem, 1.2vw, 0.8125rem);
  font-weight: 500;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
  text-shadow:
    0 1px 1px rgba(0, 0, 0, 0.85),
    0 4px 10px rgba(0, 0, 0, 0.7),
    0 14px 28px rgba(0, 0, 0, 0.55);
`;

const ExploreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.1rem;
  padding: 0.9rem 1.7rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: transparent;
  color: #fff;
  text-decoration: none;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const PageInner = styled.div`
  box-sizing: border-box;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.25rem;
  }
`;

const Band = styled.section`
  scroll-margin-top: 120px;
  padding: ${props => props.$open ? '4.25rem 0 4.75rem' : props.$tight ? '3.5rem 0 3.75rem' : '4.75rem 0'};
  border-top: ${props => props.$rule ? '1px solid rgba(16, 29, 64, 0.1)' : '0'};

  @media (max-width: 768px) {
    padding: ${props => props.$open ? '2.75rem 0 3rem' : '3rem 0'};
  }
`;

const SectionHead = styled.div`
  display: grid;
  grid-template-columns: minmax(16rem, 0.8fr) minmax(18rem, 1.2fr);
  gap: 1.25rem 4.5rem;
  align-items: start;
  margin-bottom: ${props => props.$end ? '0' : '2.75rem'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.85rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  text-align: left;
  font-weight: 500;
  line-height: 1.05;
  
  @media (max-width: 768px) {
    font-size: 2.15rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: ${props => props.$compact ? '1.15rem' : '1.45rem'};
  line-height: 1.65;
  color: ${props => props.theme.colors.secondary};
  max-width: ${props => props.$compact ? 'none' : '46rem'};
  margin: 0 0 ${props => props.$compact ? '0' : '2.5rem'};
  text-align: left;
`;

const InfoCard = styled.div`
  position: absolute;
  z-index: 1;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: min(390px, 46%);
  background: rgba(16, 29, 64, 0.72);
  backdrop-filter: blur(8px);
  color: white;
  padding: 1.6rem 1.45rem 1.7rem;
  text-align: left;

  h3 {
    color: white;
  }

  p {
    margin: 0.9rem 0 0;
    font-size: 0.98rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.84);
  }

  @media (max-width: 800px) {
    top: auto;
    right: 0;
    bottom: 0;
    left: 0;
    transform: none;
    width: auto;
  }
`;

const BadgeStatic = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.9rem;
`;

const ShopStage = styled.div`
  position: relative;
  margin-top: 2.5rem;
  border-radius: 12px;
  overflow: hidden;
  min-height: 560px;
  background: ${props => props.theme.colors.offWhite};

  @media (max-width: 800px) {
    min-height: 680px;
    margin-top: 1.75rem;
  }
`;

const ShopPhoto = styled.button`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  cursor: pointer;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 32% center;
  }
`;

const StatusBadge = styled.div`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: ${props => props.isOpen ? '#4CAF50' : '#F44336'};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.7rem;
  color: white;
  margin: 0 0 0.75rem;
  font-weight: 500;
  line-height: 1.1;
`;

const TimeInfo = styled.div`
  margin-bottom: 1.5rem;
`;

const DayLabel = styled.div`
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.95rem;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 0.35rem;
`;

const Hours = styled.div`
  color: white;
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.15rem;
  font-weight: 500;
  line-height: 1.1;
`;

const Note = styled.div`
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.9rem;
  font-style: italic;
  margin-top: 0.5rem;
`;

const StoryList = styled.div`
  position: relative;
  container-type: inline-size;
  --gap: 0.85rem;
  --col: calc((100cqw - 11 * var(--gap)) / 12);
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: var(--gap);
  align-items: start;

  > :nth-child(1) { grid-column: span 7; }
  > :nth-child(2) { grid-column: span 5; }
  > :nth-child(3) {
    grid-column: span 5;
    margin-top: calc(
      -1 * (
        (5 * var(--col) + 4 * var(--gap)) * 5 / 4
        - (7 * var(--col) + 6 * var(--gap)) * 2 / 3
      )
    );
  }
  > :nth-child(4) { grid-column: span 7; }

  @media (max-width: 800px) {
    grid-template-columns: 1fr;

    > :nth-child(1),
    > :nth-child(2),
    > :nth-child(3),
    > :nth-child(4),
    > :nth-child(5),
    > :nth-child(6) {
      grid-column: 1 / -1;
    }

    > :nth-child(3) {
      margin-top: 0;
    }
  }
`;

const LogoMark = styled.img`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  object-fit: contain;
  left: calc(5 * var(--col) + 4 * var(--gap) + 0.2rem);
  width: calc(2 * var(--col) + 3 * var(--gap) - 0.4rem);
  top: calc((7 * var(--col) + 6 * var(--gap)) * 2 / 3 + var(--gap) + 0.2rem);
  height: calc(
    (5 * var(--col) + 4 * var(--gap)) * 5 / 4
    - (7 * var(--col) + 6 * var(--gap)) * 2 / 3
    - 0.4rem
  );

  @media (max-width: 800px) {
    position: static;
    display: block;
    width: min(220px, 70%);
    height: auto;
    margin: 1.25rem auto 0;
    left: auto;
    top: auto;
    justify-self: center;
  }
`;

const GalleryButton = styled.button`
  position: absolute;
  z-index: 2;
  left: 0;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.4rem;
  height: 3.4rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary};
  color: white;
  font-size: 1.35rem;
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.colors.secondary};
  }

  @media (max-width: 800px) {
    position: static;
    margin-top: 0.85rem;
    justify-self: start;
  }
`;

const Story = styled.article`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  aspect-ratio: ${props => props.$ratio};
  background: ${props => props.theme.colors.primary};
`;

const StoryPhoto = styled.button`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  cursor: pointer;
  background: ${props => props.theme.colors.offWhite};

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${props => props.$position || 'center'};
  }
`;

const StoryBox = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 0;
  width: min(340px, 78%);
  padding: 1.15rem 1.15rem 1.25rem;
  background: rgba(16, 29, 64, 0.72);
  backdrop-filter: blur(8px);
  color: white;
  ${props => props.$side === 'right' ? 'right: 0;' : 'left: 0;'}

  h3 {
    color: white;
    font-size: 1.65rem;
    margin: 0 0 0.45rem;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.84);
    font-size: 0.98rem;
    line-height: 1.5;
  }

  @media (max-width: 800px) {
    left: 0;
    right: 0;
    width: auto;
  }
`;

const StoryTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.35rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.85rem;
  font-weight: 500;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 1.9rem;
  }
`;

const GalleryPanel = styled.div`
  box-sizing: border-box;
  width: min(920px, 100%);
  max-height: min(82vh, 760px);
  overflow: auto;
  background: ${props => props.theme.colors.offWhite};
  border-radius: 12px;
  padding: 1.15rem;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.7rem;

  > :nth-child(-n + 3) { grid-column: span 2; }
  > :nth-child(n + 4) { grid-column: span 3; }

  @media (max-width: 800px) {
    grid-template-columns: 1fr 1fr;
    > :nth-child(-n + 3),
    > :nth-child(n + 4) { grid-column: auto; }
    > :last-child { grid-column: 1 / -1; }
  }
`;

const WineSection = styled.section`
  background: white;
  border-top: 1px solid rgba(16, 29, 64, 0.1);
  border-bottom: 1px solid rgba(16, 29, 64, 0.1);
  padding: 4.5rem 0;
  margin: 0;

  @media (max-width: 768px) {
    padding: 3rem 1.25rem;
  }
`;

const WineInner = styled.div`
  box-sizing: border-box;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const WinePhotos = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 0.7rem;
  aspect-ratio: 1.7 / 1;

  @media (max-width: 800px) {
    aspect-ratio: auto;
    grid-template-columns: 1fr 1fr;
  }
`;

const WineShot = styled.button`
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: ${props => props.theme.colors.primary};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  min-height: 0;

  &:first-child {
    grid-row: 1 / span 2;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${props => props.$position || 'center'};
  }

  @media (max-width: 800px) {
    height: auto;
    aspect-ratio: 1 / 1;

    &:first-child {
      grid-row: auto;
      grid-column: 1 / -1;
      aspect-ratio: 3 / 4;
    }
  }
`;

const FeatureDescription = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.secondary};
`;

const GalleryItem = styled.button`
  display: block;
  width: 100%;
  height: ${props => props.$fill ? '100%' : 'auto'};
  padding: 0;
  border: 0;
  background: ${props => props.theme.colors.offWhite};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  aspect-ratio: ${props => props.$fill ? 'auto' : '3 / 2'};
  min-height: 0;

  ${props => props.$lead && `
    grid-row: 1 / span 2;
  `}

  @media (max-width: 800px) {
    height: auto;
    aspect-ratio: 3 / 2;
    grid-row: auto;

    ${props => props.$lead && `
      grid-column: 1 / -1;
    `}
  }
`;

const GalleryImage = styled.img`
  display: block;
  ${props => props.$zoom ? `
    position: absolute;
    width: ${props.$zoom * 100}%;
    height: auto;
    max-width: none;
    left: 50%;
    top: ${props.$top};
    transform: translateX(-50%);
  ` : `
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${props.$position || 'center'};
  `}
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(10, 20, 40, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const LightboxFigure = styled.figure`
  margin: 0;
  max-width: min(1180px, 100%);
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
  display: block;
  background: white;
  border-radius: 12px;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  border: 0;
  background: transparent;
  color: white;
  font-size: 2rem;
  line-height: 1;
  cursor: pointer;
`;

const ParkingNote = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin: 0.9rem 0 0;
  color: ${props => props.theme.colors.secondary};
  font-size: 1.05rem;
  line-height: 1.45;

  svg {
    margin-top: 0.15rem;
    color: ${props => props.theme.colors.accent};
    flex-shrink: 0;
  }
`;

const FeatureLink = styled(Link)`
  display: inline-block;
  margin-top: 1rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const UpcomingDatesSection = styled.div`
  margin: 4rem 0;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const UpcomingDatesTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const DatesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const DateCard = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  border-left: 4px solid ${props => {
    if (props.$type === 'closed') return 'rgba(244, 67, 54, 0.8)';
    if (props.$type === 'special') return 'rgba(255, 165, 0, 0.8)';
    return 'rgba(34, 139, 34, 0.8)';
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const DateTitle = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DateRange = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.8rem;
`;

const DateDetails = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const DateNote = styled.div`
  font-size: 0.9rem;
  font-style: italic;
  color: ${props => props.theme.colors.secondary};
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${props => props.theme.colors.greyBackground};
`;

const EmailSignupSection = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 4.5rem 2rem;
  text-align: center;
  width: 100%;
`;

const EmailSignupTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.4rem;
  color: white;
  margin: 0 0 1rem;
  font-weight: 500;
`;

const EmailSignupText = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  max-width: 620px;
  margin: 0 auto 2rem;
  color: rgba(255, 255, 255, 0.82);
`;

const EmailForm = styled.form`
  display: flex;
  max-width: 560px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const EmailInput = styled.input`
  flex-grow: 1;
  padding: 0.95rem 1rem;
  border: 0;
  border-radius: 0;
  font-size: 1rem;
  min-width: 0;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: -2px;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.accent};
  color: white;
  border: 0;
  border-radius: 0;
  padding: 0.95rem 1.6rem;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #865c3c;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const EmailNote = styled.p`
  max-width: 560px;
  margin: 1rem auto 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const FarmShop = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    status: null
  });
  const [activePhoto, setActivePhoto] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const location = useLocation();
  
  // Use custom hook to ensure we stay in retail section
  useRetailSection();
  
  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!activePhoto && !galleryOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (activePhoto) setActivePhoto(null);
      else setGalleryOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activePhoto, galleryOpen]);
  
  // Format current time
  const formattedTime = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const currentDay = currentTime.toLocaleDateString('en-US', { weekday: 'long' });
  
  // Get dynamic opening hours based on current date
  const getOpeningHours = () => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    
    // Christmas Day - Closed
    if (year === 2025 && month === 11 && day === 25) {
      return {
        days: "Closed",
        times: "Closed",
        note: "Christmas Day - Closed"
      };
    }
    
    // Boxing Day (Dec 26, 2025) - 9am-3:30pm
    if (year === 2025 && month === 11 && day === 26) {
      return {
        days: "Boxing Day",
        times: "09:00 - 15:30",
        note: "Special Boxing Day hours"
      };
    }
    
    // New Year's Day (Jan 1, 2026) - 9am-3:30pm
    if (year === 2026 && month === 0 && day === 1) {
      return {
        days: "New Year's Day",
        times: "09:00 - 15:30",
        note: "Special New Year's Day hours"
      };
    }
    
    // Default: Open daily 9am-5pm
    return {
      days: "Daily",
      times: "09:00 - 17:00",
      note: ""
    };
  };
  
  const openingHours = getOpeningHours();
  
  // Function to check if the farmshop is currently open
  const isShopOpen = () => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    
    // Christmas Day - Closed
    if (year === 2025 && month === 11 && day === 25) {
      return false;
    }
    
    // Parse opening hours
    const timeRange = openingHours.times.split('-').map(t => t.trim());
    if (timeRange.length !== 2 || timeRange[0] === 'Closed') return false;
    
    // Convert times to 24-hour format for comparison
    const [openHour, openMinute] = timeRange[0].split(':').map(Number);
    const [closeHour, closeMinute] = timeRange[1].split(':').map(Number);
    
    const openTime = new Date();
    openTime.setHours(openHour, openMinute || 0, 0);
    
    const closeTime = new Date();
    closeTime.setHours(closeHour, closeMinute || 0, 0);
    
    // Check if current time is between open and close times
    return currentTime >= openTime && currentTime <= closeTime;
  };
  
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailStatus({ submitting: true, error: null, success: false, status: null });

    try {
      const response = await fetch(`${config.apiEndpoint}/newsletter/v1/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        body: JSON.stringify({
          firstName: '',
          lastName: '',
          email,
          origin: window.location.origin,
          tags: ['Farm Shop']
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Subscription failed');
      }

      setEmailStatus({
        submitting: false,
        error: null,
        success: true,
        status: data.status
      });

      if (data.status === 'new_subscription') setEmail('');
    } catch (error) {
      setEmailStatus({
        submitting: false,
        error: error.message || 'An error occurred while subscribing. Please try again.',
        success: false,
        status: null
      });
    }
  };
  
  return (
    <FarmshopContainer>
      <Hero>
        <HeroImage src={farmshopBanImage} alt="" />
        <HeroOverlay />
        <HeroInner>
          <HeroHeading>
            <HeroLogo src={farmshopLogo} alt="Belvoir Farm Shop" />
          </HeroHeading>
          <HeroSubtitle>Fresh, local produce from our estate to your table</HeroSubtitle>
          <ExploreButton
            href="#shop"
            onClick={(event) => {
              event.preventDefault();
              const target = document.getElementById('shop');
              if (!target) return;
              const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
            }}
          >
            Explore the shop
          </ExploreButton>
        </HeroInner>
      </Hero>
      
      <Band $open id="shop">
        <PageInner>
        <IntroText>
          Our Farm Shop offers the finest selection of fresh, seasonal produce from the Belvoir Estate and carefully selected local suppliers. 
          From organic vegetables to artisanal cheeses, our shop is a celebration of the rich agricultural heritage of the Vale of Belvoir.
        </IntroText>
        <ShopStage>
          <ShopPhoto
            type="button"
            aria-label="Inside Belvoir Farm Shop"
            onClick={() => setActivePhoto({ src: farmshopImage, alt: 'Inside Belvoir Farm Shop' })}
          >
            <img src={farmshopImage} alt="" />
          </ShopPhoto>
          <InfoCard>
            <BadgeStatic>
              <StatusBadge isOpen={isShopOpen()}>
                <FontAwesomeIcon icon={faClock} />
                {formattedTime} on {currentDay}
              </StatusBadge>
              <StatusBadge isOpen={isShopOpen()}>
                {isShopOpen() ? 'Open Now' : 'Closed'}
              </StatusBadge>
            </BadgeStatic>
            <CardTitle>Farm Shop Opening Hours</CardTitle>
            <TimeInfo>
              <DayLabel>{openingHours.days}</DayLabel>
              <Hours>{openingHours.times}</Hours>
              <Note>{openingHours.note}</Note>
            </TimeInfo>
            <p>
              Visit our Farm Shop in person to explore our full range of products. Located in the Retail Village at Belvoir Castle, 
              our shop is easily accessible with ample parking nearby.
            </p>
          </InfoCard>
        </ShopStage>
        </PageInner>
      </Band>
        
        {/* <UpcomingDatesSection>
          <UpcomingDatesTitle>
            <FontAwesomeIcon icon={faCalendarAlt} />
            Upcoming Important Dates
          </UpcomingDatesTitle>
          <DatesList>
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Normal Opening Hours
              </DateTitle>
              <DateRange>Daily</DateRange>
              <DateDetails>
                <strong>Farm Shop:</strong> 9:00am - 5:00pm daily
              </DateDetails>
            </DateCard>
            
            <DateCard $type="closed">
              <DateTitle>
                <FontAwesomeIcon icon={faTimes} />
                Christmas Day
              </DateTitle>
              <DateRange>Thursday 25th December 2025</DateRange>
              <DateDetails>
                <strong>Closed</strong>
              </DateDetails>
              <DateNote>We will be closed for Christmas Day</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                Boxing Day
              </DateTitle>
              <DateRange>Friday 26th December 2025</DateRange>
              <DateDetails>
                <strong>Farm Shop:</strong> 9:00am - 3:30pm
              </DateDetails>
              <DateNote>Special Boxing Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faInfoCircle} />
                New Year's Day
              </DateTitle>
              <DateRange>Thursday 1st January 2026</DateRange>
              <DateDetails>
                <strong>Farm Shop:</strong> 9:00am - 3:30pm
              </DateDetails>
              <DateNote>Special New Year's Day hours - early closing at 3:30pm</DateNote>
            </DateCard>
            
            <DateCard $type="special">
              <DateTitle>
                <FontAwesomeIcon icon={faCheck} />
                Online Shop Now Open
              </DateTitle>
              <DateRange>Now Available</DateRange>
              <DateDetails>
                <strong>Online Shop:</strong> Now open for Click & Collect orders
              </DateDetails>
              <DateNote>
                <Link to="/farm-shop" style={{ color: '#9d6d49', textDecoration: 'none', fontWeight: '600' }}>
                  Shop online now →
                </Link>
              </DateNote>
            </DateCard>
          </DatesList>
        </UpcomingDatesSection> */}

      <Band $rule>
        <PageInner>
        <SectionHead>
          <SectionTitle>What We Offer</SectionTitle>
        </SectionHead>
        
        <StoryList>
          {STORIES.map((story) => (
            <Story key={story.title} $ratio={story.ratio}>
              <StoryPhoto
                type="button"
                $position={story.position || story.photo.position}
                aria-label={story.photo.alt}
                onClick={() => setActivePhoto(story.photo)}
              >
                <img
                  src={story.photo.src}
                  alt=""
                  loading="lazy"
                />
              </StoryPhoto>
              <StoryBox $side={story.side}>
                <StoryTitle>{story.title}</StoryTitle>
                <FeatureDescription>{story.text}</FeatureDescription>
              </StoryBox>
            </Story>
          ))}
          <LogoMark src={farmshopLogo} alt="Belvoir Farm Shop" />
          <GalleryButton type="button" aria-label="More photos" onClick={() => setGalleryOpen(true)}>
            <FontAwesomeIcon icon={faCamera} />
          </GalleryButton>
        </StoryList>
        </PageInner>
      </Band>

      <WineSection>
        <WineInner>
          <SectionHead>
            <SectionTitle>Belvoir Wines</SectionTitle>
            <div>
              <FeatureDescription>
                Browse our estate wines in the Farm Shop. The vineyard tour begins here, with a guided tasting of four estate wines.
              </FeatureDescription>
              <FeatureLink to="/about/belvoir-wines" style={{ marginRight: '1.5rem' }}>
                Discover Belvoir Wines
                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.45rem' }} />
              </FeatureLink>
              <FeatureLink to="/whats-on/vineyard-tour">
                Vineyard tour
                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.45rem' }} />
              </FeatureLink>
            </div>
          </SectionHead>
          <WinePhotos>
            {WINE_PHOTOS.map((photo) => (
              <WineShot
                key={photo.src}
                type="button"
                $position={photo.position}
                onClick={() => setActivePhoto(photo)}
              >
                <img src={photo.src} alt={photo.alt} loading="lazy" />
              </WineShot>
            ))}
          </WinePhotos>
        </WineInner>
      </WineSection>

      <Band $tight>
        <PageInner>
          <SectionHead $end>
            <SectionTitle>Visit Us In Person</SectionTitle>
            <div>
              <IntroText $compact>
                Located in the Retail Village at Belvoir Castle, our shop offers a warm welcome and a chance to discover the finest produce from our estate and local suppliers.
              </IntroText>
              <ParkingNote>
                <FontAwesomeIcon icon={faCar} />
                Free parking for up to 2.5 hours, beside the shops
              </ParkingNote>
              <FeatureLink to="/retail-village/directions">
                Retail Village directions
                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.45rem' }} />
              </FeatureLink>
            </div>
          </SectionHead>
        </PageInner>
      </Band>

      <EmailSignupSection>
        <EmailSignupTitle>Stay Updated</EmailSignupTitle>
        <EmailSignupText>
          Sign up to receive updates about new products, seasonal specials, and exclusive offers from Belvoir Farm Shop.
        </EmailSignupText>
        <EmailForm onSubmit={handleEmailSubmit}>
          <EmailInput
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <SubmitButton type="submit" disabled={emailStatus.submitting}>
            {emailStatus.submitting ? 'Signing up...' : 'Sign Up'}
          </SubmitButton>
        </EmailForm>
        {emailStatus.error && <EmailNote>{emailStatus.error}</EmailNote>}
        {emailStatus.success && (
          <EmailNote>
            {emailStatus.status === 'already_subscribed'
              ? 'You are already subscribed to our Farm Shop newsletter!'
              : emailStatus.status === 'pending_confirmation'
                ? 'Thank you for subscribing! Please check your email to confirm your subscription.'
                : emailStatus.status === 'new_subscription'
                  ? 'Welcome to the Farm Shop newsletter! You have been successfully subscribed.'
                  : 'Thank you for subscribing to our Farm Shop newsletter!'}
          </EmailNote>
        )}
      </EmailSignupSection>

      {galleryOpen && (
        <Lightbox onClick={() => setGalleryOpen(false)} role="dialog" aria-modal="true" aria-label="More Farm Shop photographs">
          <LightboxClose type="button" aria-label="Close" onClick={() => setGalleryOpen(false)}>×</LightboxClose>
          <GalleryPanel onClick={(event) => event.stopPropagation()}>
            {MORE_PHOTOS.map((photo) => (
              <GalleryItem
                key={photo.src}
                type="button"
                onClick={() => setActivePhoto(photo)}
              >
                <GalleryImage
                  src={photo.src}
                  alt={photo.alt}
                  $position={photo.position}
                  loading="lazy"
                />
              </GalleryItem>
            ))}
          </GalleryPanel>
        </Lightbox>
      )}

      {activePhoto && (
        <Lightbox onClick={() => setActivePhoto(null)} role="dialog" aria-modal="true" aria-label={activePhoto.alt}>
          <LightboxClose type="button" aria-label="Close" onClick={() => setActivePhoto(null)}>×</LightboxClose>
          <LightboxFigure onClick={(event) => event.stopPropagation()}>
            <LightboxImage src={activePhoto.src} alt={activePhoto.alt} />
          </LightboxFigure>
        </Lightbox>
      )}
      
      <Footer />
    </FarmshopContainer>
  );
};

export default FarmShop;